/* eslint-disable react-hooks/exhaustive-deps */
import {
  CheckCircleFilled,
  CreditCardOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  GlobalOutlined,
  IdcardOutlined,
  LoadingOutlined,
  PieChartOutlined,
  PoundCircleOutlined,
  ShoppingOutlined,
  SyncOutlined,
  WalletOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { Button, ConfigProvider, Input, Modal, Popover } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { sticky_config } from '../../components/config';
import { clearOrderBroadcast } from '../../hooks/useOrderBroadcast';
import { useScanCapture } from '../../hooks/useScanCapture';
import {
  checkPaymentStatus,
  createPaymentIntent,
  getIntentUrl,
  getOrdersById,
  postOrderToNetSuite,
  updateOrder,
} from '../../redux/actions/order';
import { collectCashPayment, getTerminalAvailability } from '../../redux/actions/payment';
import { getRoutes } from '../../routes/Routes';
import { formatCurrency, getStickyPaymentUrl } from '../../utils';
import { printReceipt, printReceiptCopies } from '../../utils/printReceipt';
import { errorToast } from '../../utils/toast';
import { useKioskIdentity } from './useKioskIdentity';
import { TERMINAL_OUTCOME, useTerminalPayment } from './useTerminalPayment';


const to2 = (num) => Number(parseFloat(num || 0).toFixed(2));

const KioskCheckoutModal = ({
  isOpen,
  onClose,
  customer,
  order,
  cartItems = [],
  subtotal = 0,
  taxTotal = 0,
  grandTotal: rawGrandTotal = 0,
  onPaymentProcessingChange,
  guestInfo,
  onTerminalReferenceChange,
}) => {
  const grandTotal = to2(rawGrandTotal);

  const navigate = useNavigate();
  const { storeId, terminalId, deviceToken, dual } = useKioskIdentity();
  const [checkoutStep, setCheckoutStep] = useState('review');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentErrorMessage, setPaymentErrorMessage] = useState('');
  const [cashAuthError, setCashAuthError] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [terminalAvailable, setTerminalAvailable] = useState(false);
  const [cashAmount, setCashAmount] = useState(0);
  const [cardAmount, setCardAmount] = useState(0);
  const [onlineAmount, setOnlineAmount] = useState(0);
  const [processingAmount, setProcessingAmount] = useState(0);
  const [currentProcessingType, setCurrentProcessingType] = useState('card');
  const [onAccountAmount, setOnAccountAmount] = useState(0);
  const isAccountCustomer = !!(customer?.code && (Number(customer?.creditLimit) > 0 || customer?.terms));
  const availableCredit = customer?.availableCredit !== undefined ? customer.availableCredit : Math.max(0, (Number(customer?.creditLimit) || 0) - (Number(customer?.currentBalance || customer?.balance) || 0));

  const balancePopoverContent = (
    <div style={{ width: '280px', padding: '8px' }}>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#111827', borderBottom: '1px solid #E5E7EB', paddingBottom: '8px' }}>Account Balance</h3>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
        <span style={{ color: '#6B7280', fontSize: '14px' }}>Credit Limit</span>
        <span style={{ fontWeight: 600, color: '#111827', fontSize: '14px' }}>{formatCurrency(customer?.creditLimit || 0)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
        <span style={{ color: '#6B7280', fontSize: '14px' }}>Current Balance</span>
        <span style={{ fontWeight: 600, color: '#F58220', fontSize: '14px' }}>{formatCurrency(customer?.currentBalance || customer?.balance || 0)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #E5E7EB', paddingTop: '12px', marginTop: '4px' }}>
        <span style={{ color: '#111827', fontSize: '14px', fontWeight: 600 }}>Available Credit</span>
        <span style={{ fontWeight: 700, color: '#10B981', fontSize: '15px' }}>{formatCurrency(availableCredit)}</span>
      </div>
    </div>
  );
  const [cardMethod, setCardMethod] = useState('online');

  useEffect(() => {
    if (!isOpen || !storeId) return;
    getTerminalAvailability({ storeId, deviceToken }).then((res) => {
      setTerminalAvailable(Boolean(res?.ack && res?.available));
    });
  }, [isOpen, storeId, deviceToken]);

  const paymentPopupRef = useRef(null);
  const pollingActiveRef = useRef(false);
  const pollTimerRef = useRef(null);
  const orderRef = useRef(order);
  const { collect: collectTerminal, cancelPending: cancelTerminalPending } =
    useTerminalPayment(onTerminalReferenceChange);

  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  const closePaymentWindow = () => {
    if (paymentPopupRef.current) {
      try {
        paymentPopupRef.current.close();
      } catch (e) {
        console.error('Error closing paymentPopupRef:', e);
      }
      paymentPopupRef.current = null;
    }
  };

  useEffect(() => {
    if (checkoutStep === 'processing' || checkoutStep === 'success') {
      closePaymentWindow();
      const timer = setTimeout(() => {
        closePaymentWindow();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [checkoutStep]);

  useEffect(() => {
    if (isOpen) {
      setCheckoutStep('review');
      setIsSubmitting(false);
      setPaymentErrorMessage('');
    } else {
      cleanupPayment();
    }
  }, [isOpen, customer]);

  const isPaymentProcessing =
    checkoutStep === 'payment' ||
    checkoutStep === 'payment_method' ||
    checkoutStep === 'cash_auth' ||
    checkoutStep === 'processing' ||
    checkoutStep === 'success';

  useEffect(() => {
    if (onPaymentProcessingChange) {
      onPaymentProcessingChange(isPaymentProcessing);
    }
  }, [isPaymentProcessing, onPaymentProcessingChange]);

  const cleanupPayment = () => {
    pollingActiveRef.current = false;
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    closePaymentWindow();
  };

  useEffect(() => {
    return () => {
      cleanupPayment();
      if (onPaymentProcessingChange) {
        onPaymentProcessingChange(false);
      }
    };
  }, [onPaymentProcessingChange]);


  const cancelPayment = () => {
    cleanupPayment();
    cancelTerminalPending();
    setIsSubmitting(false);
    setCheckoutStep('review');
  };

  const finalizeOrder = async (targetOrderId, orderCode, paymentMeta = {}, overrideAmounts = null) => {
    const { method = 'card', attendantName = '', gateway, cardDetail } = paymentMeta;

    const finalCash = overrideAmounts?.cash !== undefined ? overrideAmounts.cash : (cashAmount || 0);
    const finalCard = overrideAmounts?.card !== undefined ? overrideAmounts.card : (cardAmount || 0);
    const finalOnline = overrideAmounts?.online !== undefined ? overrideAmounts.online : (onlineAmount || 0);
    const finalAccount = overrideAmounts?.account !== undefined ? overrideAmounts.account : (onAccountAmount || 0);

    closePaymentWindow();
    try {
      setCheckoutStep('processing');
      setIsSubmitting(true);
      closePaymentWindow();

      await updateOrder(
        {
          id: targetOrderId,
          cash_payment: finalCash,
          card_payment: finalCard,
          online_payment: finalOnline,
          on_account_payment: finalAccount,
          ...(!customer?.code && guestInfo?.name ? { kiosk_customer_name: guestInfo.name } : {}),
          ...(!customer?.code && guestInfo?.email ? { kiosk_customer_email: guestInfo.email } : {}),
          ...(!customer?.code && guestInfo?.phone ? { kiosk_customer_phone: guestInfo.phone } : {}),
        },
        { isGet: false }
      );

      let finalInvoiceNumber = orderRef.current?.invoice_code || orderCode;
      try {
        let postRes = await postOrderToNetSuite({ id: targetOrderId }, { toast: false });

        if (!postRes?.ack && postRes?.duplicate_orders?.length) {
          postRes = await postOrderToNetSuite(
            { id: targetOrderId, bypass_duplicate_check: 1 },
            { toast: false }
          );
        }

        if (postRes?.order?.invoice_code) {
          finalInvoiceNumber = postRes.order.invoice_code;
        } else if (!postRes?.ack) {
          console.warn('Order NetSuite posting failed, but card payment succeeded:', postRes);
        }
      } catch (postErr) {
        console.error('Error posting order to NetSuite:', postErr);
      }

      setCheckoutStep('success');
      closePaymentWindow();
      clearOrderBroadcast();
      try {
        (dual ? printReceiptCopies : printReceipt)({
          invoiceNumber: finalInvoiceNumber,
          customer: {
            title: customer?.title || guestInfo?.name || 'Guest',
            code: customer?.code,
            email: customer?.invoice_email || guestInfo?.email || '',
            phone: customer?.phone || guestInfo?.phone || '',
          },
          orderItems: cartItems,
          kiosk: { storeId, terminalId },
          totals: {
            subtotal,
            vat: taxTotal,
            discount: 0,
            grandTotal,
            remaining_amount: 0,
            card: finalCard,
            online_payment: finalOnline,
            cash: finalCash,
            on_account_payment: finalAccount,
            auth_code: cardDetail?.authCode || '',
            attendant_name: method === 'cash' ? attendantName : '',
            paymentDetail: gateway ? { gateway, ...cardDetail } : null,
          },
        });
      } catch (printErr) {
        console.error('Error printing receipt:', printErr);
      }

      setTimeout(() => {
        if (customer) {
          navigate({ pathname: getRoutes().account_kiosk.route(), search: 'redirectToHome=true' }, { replace: true });
        } else {
          sessionStorage.removeItem('kiosk_guest_info');
          navigate({ pathname: getRoutes().kiosk.route(), search: 'redirectToHome=true' }, { replace: true });
          window.navigation?.reload?.();
        }
      }, 4000);
    } catch (err) {
      console.error('Error finalizing order:', err);
      setCheckoutStep('success');
      clearOrderBroadcast();
      setTimeout(() => {
        if (customer) {
          navigate({ pathname: getRoutes().account_kiosk.route(), search: 'redirectToHome=true' }, { replace: true });
        } else {
          sessionStorage.removeItem('kiosk_guest_info');
          navigate({ pathname: getRoutes().kiosk.route(), search: 'redirectToHome=true' }, { replace: true });
          window.navigation?.reload?.();
        }
      }, 4000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const pollPaymentStatus = async ({
    magna_id,
    order_id,
    targetOrderId,
    orderCode,
    amount,
    processingType,
    overrideAmounts,
  }) => {
    if (!pollingActiveRef.current) return;

    try {
      const res = await checkPaymentStatus({
        app_id: sticky_config?.appId,
        magna_id,
        order_id,
      });

      if (!pollingActiveRef.current) return;

      const statusCode =
        typeof res?.status === 'object' ? res?.status?.status : res?.status;

      if (statusCode === 1 || (res?.success && (statusCode === 1 || res?.status?.status === 1))) {
        closePaymentWindow();
        cleanupPayment();
        if (onlineAmount > 0 && processingType === 'card') {
          setIsSubmitting(false);
          handleOnlinePay(onlineAmount, 'online');
        } else if (cashAmount > 0) {
          setCheckoutStep('cash_auth');
          setIsSubmitting(false);
        } else {
          await finalizeOrder(targetOrderId, orderCode, {
            gateway: 'sticky',
            cardDetail: { reference: res?.reference, sessionId: res?.sessionId, paidAt: res?.paidAt },
          }, overrideAmounts);
        }
        return;
      } else if (statusCode === 2) {
        setPaymentErrorMessage('Payment was declined or failed. Please check your card and try again.');
        cleanupPayment();
        setIsSubmitting(false);
        setCheckoutStep('payment_failed');
        return;
      } else if (statusCode === 3) {
        cleanupPayment();
        setIsSubmitting(false);
        setCheckoutStep('payment_failed');
        setPaymentErrorMessage('Payment was cancelled on the card reader.');
        return;
      } else {
        pollTimerRef.current = setTimeout(() => {
          pollPaymentStatus({
            magna_id,
            order_id,
            targetOrderId,
            orderCode,
            amount,
            processingType,
            overrideAmounts,
          });
        }, 3000);
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      if (!pollingActiveRef.current) return;

      pollTimerRef.current = setTimeout(() => {
        pollPaymentStatus({
          magna_id,
          order_id,
          targetOrderId,
          orderCode,
          amount,
          processingType,
          overrideAmounts,
        });
      }, 3500);
    }
  };

  const resolveOrderContext = async () => {
    const activeOrderId = order?.id;
    if (!activeOrderId) {
      errorToast('No active order found.');
      return null;
    }
    if (!cartItems.length || grandTotal <= 0) {
      errorToast('Cart is empty or invalid amount.');
      return null;
    }

    let currentOrder = order;
    if (!currentOrder?.id) {
      const res = await getOrdersById(activeOrderId, { toast: false });
      currentOrder = res?.order || null;
    }

    return {
      activeOrderId,
      orderCode: currentOrder?.code || currentOrder?.saleOrderNo || String(activeOrderId),
      customerCode: customer?.code || currentOrder?.customerCode || '',
      customerNetsuiteId: currentOrder?.customer_netsuite_id || customer?.customer_netsuite_id,
    };
  };

  const handleTerminalPay = async (amount, overrideAmounts = null) => {
    if (isSubmitting) return;
    const ctx = await resolveOrderContext();
    if (!ctx) return;

    setIsSubmitting(true);
    setPaymentErrorMessage('');
    setCardMethod('terminal');
    setProcessingAmount(amount);
    setCurrentProcessingType('card');

    try {
      setCheckoutStep('payment');
      const result = await collectTerminal({ orderId: ctx.activeOrderId, storeId, deviceToken });

      if (result.outcome === TERMINAL_OUTCOME.APPROVED) {
        if (onlineAmount > 0) {
          setIsSubmitting(false);
          handleOnlinePay(onlineAmount, 'online', overrideAmounts);
        } else if (cashAmount > 0) {
          setCheckoutStep('cash_auth');
          setIsSubmitting(false);
        } else {
          await finalizeOrder(ctx.activeOrderId, ctx.orderCode, {
            gateway: 'worldpay',
            cardDetail: result.card,
          }, overrideAmounts);
        }
        return;
      }

      const messages = {
        [TERMINAL_OUTCOME.TIMEOUT]: 'Payment timed out. Please try again.',
        [TERMINAL_OUTCOME.CANCELLED]: 'Payment was cancelled.',
        [TERMINAL_OUTCOME.NOT_CONFIGURED]: 'Card terminal is not available right now. Please choose another payment method.',
      };
      setPaymentErrorMessage(
        messages[result.outcome] ||
        result.message ||
        'Payment was declined or failed. Please check your card and try again.'
      );
      setIsSubmitting(false);
      setCheckoutStep('payment_failed');
    } catch (err) {
      console.error('Failed to initiate terminal payment:', err);
      errorToast('Failed to initiate payment. Please try again.');
      setIsSubmitting(false);
      setCheckoutStep('review');
    }
  };

  const handleOnlinePay = async (amount, type = 'online', overrideAmounts = null) => {
    if (isSubmitting) return;
    const ctx = await resolveOrderContext();
    if (!ctx) return;

    setIsSubmitting(true);
    setPaymentErrorMessage('');
    setCardMethod('online');
    setProcessingAmount(amount);
    setCurrentProcessingType(type);

    try {
      const randomId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : uuidv4();
      const formattedAmount = Math.round(parseFloat(amount || 0) * 100);

      const mainUrl = await getIntentUrl();
      if (!mainUrl) {
        errorToast('Failed to connect to payment gateway. Please try again.');
        setIsSubmitting(false);
        return;
      }

      const url = getStickyPaymentUrl({
        mainUrl,
        amount: formattedAmount,
        orderId: ctx.orderCode,
        customerId: ctx.customerCode,
        customerNetsuiteId: ctx.customerNetsuiteId,
        magnaId: randomId,
      });

      const intentRes = await createPaymentIntent({
        app_id: sticky_config?.appId,
        magna_id: randomId,
        order_id: ctx.orderCode,
        customer_id: ctx.customerCode,
        customer_netsuite_id: ctx.customerNetsuiteId,
        transaction_amount: amount,
      });

      if (!intentRes?.success) {
        closePaymentWindow();
        errorToast('Failed to initialize payment session. Please try again.');
        setIsSubmitting(false);
        return;
      }

      if (url) {
        try {
          if (paymentPopupRef.current && !paymentPopupRef.current.closed) {
            paymentPopupRef.current.location.href = url;
          } else {
            paymentPopupRef.current = window.open(
              url,
              '_blank',
              'toolbar=no,location=no,menubar=no,status=no,width=520,height=680'
            );
          }
        } catch (e) {
          paymentPopupRef.current = window.open(
            url,
            '_blank',
            'toolbar=no,location=no,menubar=no,status=no,width=520,height=680'
          );
        }
      }

      setCheckoutStep('payment');
      pollingActiveRef.current = true;

      pollPaymentStatus({
        magna_id: randomId,
        order_id: ctx.orderCode,
        targetOrderId: ctx.activeOrderId,
        orderCode: ctx.orderCode,
        amount: amount,
        processingType: type,
        overrideAmounts,
      });
    } catch (err) {
      closePaymentWindow();
      console.error('Failed to initiate payment:', err);
      errorToast('Failed to initiate payment. Please try again.');
      setIsSubmitting(false);
      setCheckoutStep('review');
    }
  };

  const handleCardRetry = () => (cardMethod === 'terminal' ? handleTerminalPay(processingAmount) : handleOnlinePay(processingAmount, currentProcessingType));

  const handleCashScan = async (card_code) => {
    if (isSubmitting) return;

    const activeOrderId = order?.id;
    if (!activeOrderId) {
      setCashAuthError('No active order found.');
      return;
    }

    setIsSubmitting(true);
    setCashAuthError('');

    try {
      let currentOrder = order;
      if (!currentOrder?.id) {
        const res = await getOrdersById(activeOrderId, { toast: false });
        currentOrder = res?.order || null;
      }
      const orderCode = currentOrder?.code || currentOrder?.saleOrderNo || String(activeOrderId);

      const res = await collectCashPayment({ orderId: activeOrderId, card_code });

      if (!res?.ack) {
        setCashAuthError(res?.message || 'Card not recognized. Please try again.');
        setManualCode('');
        setIsSubmitting(false);
        return;
      }

      setManualCode('');
      await finalizeOrder(activeOrderId, orderCode, {
        method: 'cash',
        attendantName: res?.attendant?.name || '',
        gateway: 'cash',
        cardDetail: { reference: res?.reference, approvedAt: new Date().toISOString() },
      });
    } catch (err) {
      console.error('Failed to collect cash payment:', err);
      setCashAuthError('Failed to authorize cash payment. Please try again.');
      setManualCode('');
      setIsSubmitting(false);
    }
  };

  useScanCapture(handleCashScan, { enabled: checkoutStep === 'cash_auth' && !isSubmitting });

  return (
    <Modal
      open={isOpen}
      maskClosable={false}
      onCancel={() => {
        if (!isSubmitting && checkoutStep !== 'payment' && checkoutStep !== 'cash_auth' && checkoutStep !== 'processing' && checkoutStep !== 'success') {
          cleanupPayment();
          onClose();
        }
      }}
      footer={null}
      closable={false}
      centered
      className="kiosk-checkout-modal"
      width={480}
      zIndex={2000}
      styles={{
        body: { padding: 0 },
      }}
    >
      <div className="kiosk-modal-content">

        {checkoutStep === 'review' && (
          <>
            <div className="kiosk-modal-header">
              <ShoppingOutlined style={{ fontSize: '48px', color: '#F58220' }} />
              <h2>Review Your Order</h2>
            </div>

            <div className="kiosk-modal-body">
              <div className="kiosk-modal-summary">
                <div className="kiosk-modal-summary-row">
                  <span>Total Items:</span>
                  <span style={{ fontWeight: 600 }}>
                    {cartItems.reduce(
                      (acc, item) =>
                        acc + (item.quantity === '' ? 0 : item.quantity),
                      0
                    )}
                  </span>
                </div>
                <div className="kiosk-modal-summary-row">
                  <span>Subtotal
                    <span className="vat-label ">(Exc. VAT)</span>
                  </span>
                  <span style={{ fontWeight: 600 }}>
                    {formatCurrency(subtotal)}
                  </span>
                </div>
                <div className="kiosk-modal-summary-row">
                  <span>VAT</span>
                  <span style={{ fontWeight: 600 }}>
                    {formatCurrency(taxTotal)}
                  </span>
                </div>
                <div className="kiosk-modal-summary-row total">
                  <span>Total
                    <span className="vat-label">(Inc. VAT)</span>
                  </span>
                  <span>{formatCurrency(grandTotal)}</span>
                </div>
              </div>
            </div>

            <div className="kiosk-modal-footer">
              <button
                type="button"
                className="kiosk-modal-btn cancel"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="kiosk-modal-btn confirm"
                onClick={() => setCheckoutStep('payment_type')}
                disabled={isSubmitting}
              >
                Continue to Payment
              </button>
            </div>
          </>
        )}


        {checkoutStep === 'payment_type' && (
          <>
            <div className="kiosk-modal-header">
              <ShoppingOutlined style={{ fontSize: '48px', color: '#F58220' }} />
              <h2>Payment Type</h2>
            </div>

            <div className="kiosk-modal-body" style={{ padding: '24px 20px' }}>
              <div style={{ fontSize: '18px', fontWeight: 500, color: '#4B5563', textAlign: 'center', marginBottom: '24px' }}>
                How would you like to pay?
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  className="kiosk-pay-option"
                  onClick={() => setCheckoutStep('payment_method_full')}
                >
                  <PoundCircleOutlined style={{ fontSize: '32px', color: '#F58220' }} />
                  <span style={{ fontSize: '16px' }}>Pay Full Amount</span>
                </button>

                <button
                  type="button"
                  className="kiosk-pay-option"
                  onClick={() => setCheckoutStep('payment_method_split')}
                >
                  <PieChartOutlined style={{ fontSize: '32px', color: '#F58220' }} />
                  <span style={{ fontSize: '16px' }}>Split Payment</span>
                </button>
              </div>
            </div>

            <div className="kiosk-modal-footer">
              <button
                type="button"
                className="kiosk-modal-btn cancel"
                onClick={() => setCheckoutStep('review')}
                style={{ width: '100%' }}
              >
                Back to Order
              </button>
            </div>
          </>
        )}


        {checkoutStep === 'payment_method_full' && (
          <>
            <div className="kiosk-modal-header">
              <ShoppingOutlined style={{ fontSize: '48px', color: '#F58220' }} />
              <h2>Pay in Full</h2>
            </div>

            <div className="kiosk-modal-body" style={{ padding: '24px 20px' }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ fontSize: '15px', color: '#6B7280', fontWeight: 500, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Due</div>
                <div style={{ fontSize: '36px', fontWeight: 800, color: '#F58220' }}>{formatCurrency(grandTotal)}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                {terminalAvailable && (
                  <button
                    type="button"
                    className="kiosk-pay-option"
                    onClick={() => {
                      setCardAmount(to2(grandTotal));
                      setOnlineAmount(0);
                      setCashAmount(0);
                      handleTerminalPay(grandTotal, { card: to2(grandTotal), online: 0, cash: 0, account: 0 });
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting && cardMethod === 'terminal' ? (
                      <LoadingOutlined spin style={{ fontSize: '28px', color: '#F58220' }} />
                    ) : (
                      <CreditCardOutlined style={{ fontSize: '28px', color: '#F58220' }} />
                    )}
                    <span>Pay by Card</span>
                  </button>
                )}
                <button
                  type="button"
                  className="kiosk-pay-option"
                  onClick={() => {
                    let overrideCard = 0;
                    let overrideOnline = 0;
                    if (terminalAvailable) {
                      setOnlineAmount(to2(grandTotal));
                      setCardAmount(0);
                      overrideOnline = to2(grandTotal);
                    } else {
                      setCardAmount(to2(grandTotal));
                      setOnlineAmount(0);
                      overrideCard = to2(grandTotal);
                    }
                    setCashAmount(0);
                    setOnAccountAmount(0);
                    handleOnlinePay(grandTotal, terminalAvailable ? 'online' : 'card', { card: overrideCard, online: overrideOnline, cash: 0, account: 0 });
                  }}
                  disabled={isSubmitting}
                >
                  {isSubmitting && cardMethod === 'online' ? (
                    <LoadingOutlined spin style={{ fontSize: '28px', color: '#F58220' }} />
                  ) : terminalAvailable ? (
                    <GlobalOutlined style={{ fontSize: '28px', color: '#F58220' }} />
                  ) : (
                    <CreditCardOutlined style={{ fontSize: '28px', color: '#F58220' }} />
                  )}
                  <span>{terminalAvailable ? 'Pay by Link' : 'Pay by Card'}</span>
                </button>
                <button
                  type="button"
                  className="kiosk-pay-option"
                  style={{ gridColumn: (terminalAvailable && !isAccountCustomer) ? 'span 2' : 'auto' }}
                  onClick={() => {
                    setCashAmount(to2(grandTotal));
                    setCardAmount(0);
                    setOnlineAmount(0);
                    setCashAuthError('');
                    setCheckoutStep('cash_auth');
                  }}
                  disabled={isSubmitting}
                >
                  <WalletOutlined style={{ fontSize: '28px', color: '#F58220' }} />
                  <span>Pay with Cash</span>
                </button>
                {isAccountCustomer && (
                  <div style={{ position: 'relative', gridColumn: !terminalAvailable ? 'span 2' : 'auto', display: 'flex' }}>
                    <button
                      type="button"
                      className="kiosk-pay-option"
                      style={{
                        opacity: (grandTotal > availableCredit) ? 0.6 : 1,
                        width: '100%',
                        margin: 0
                      }}
                      onClick={() => {
                        if (grandTotal > availableCredit) return;
                        setOnAccountAmount(to2(grandTotal));
                        setCashAmount(0);
                        setCardAmount(0);
                        setOnlineAmount(0);
                        finalizeOrder(order?.id, order?.code || String(order?.id), { method: 'on_account' }, { account: to2(grandTotal), card: 0, online: 0, cash: 0 });
                      }}
                      disabled={isSubmitting || grandTotal > availableCredit}
                    >
                      <FileTextOutlined style={{ fontSize: '28px', color: (grandTotal > availableCredit) ? '#9CA3AF' : '#F58220' }} />
                      <span style={{ color: (grandTotal > availableCredit) ? '#9CA3AF' : 'inherit', marginTop: '4px' }}>Pay On Account</span>
                      {grandTotal > availableCredit && (
                        <span style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px' }}>Insufficient Credit</span>
                      )}
                    </button>
                    <Popover content={balancePopoverContent} trigger="click" placement="top">
                      <div
                        style={{ position: 'absolute', top: '10px', right: '10px', cursor: 'pointer', zIndex: 10, padding: '4px' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <InfoCircleOutlined style={{ fontSize: '20px', color: '#9CA3AF', transition: 'color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.color = '#F58220'} onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'} />
                      </div>
                    </Popover>
                  </div>
                )}
              </div>
            </div>

            <div className="kiosk-modal-footer">
              <button
                type="button"
                className="kiosk-modal-btn cancel"
                onClick={() => setCheckoutStep('payment_type')}
                disabled={isSubmitting}
                style={{ width: '100%', fontSize: '16px', padding: '14px' }}
              >
                Back
              </button>
            </div>
          </>
        )}

        {checkoutStep === 'payment_method_split' && (
          <>
            <div className="kiosk-modal-header">
              <ShoppingOutlined style={{ fontSize: '48px', color: '#F58220' }} />
              <h2>Split Payment</h2>
            </div>

            <div className="kiosk-modal-body" style={{ padding: '24px 20px' }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ fontSize: '15px', color: '#6B7280', fontWeight: 500, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Remaining Balance</div>
                <div style={{ fontSize: '36px', fontWeight: 800, color: '#F58220' }}>
                  {formatCurrency(Math.max(0, to2(grandTotal) - to2(cashAmount) - to2(cardAmount) - to2(onlineAmount) - to2(onAccountAmount)))}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '420px', margin: '0 auto' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'stretch',
                  border: '2px solid ' + (cardAmount > 0 ? '#F58220' : '#E5E7EB'),
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                  transition: 'all 0.2s',
                  boxShadow: cardAmount > 0 ? '0 4px 12px rgba(245, 130, 32, 0.15)' : 'none'
                }}>
                  <div style={{
                    width: '110px',
                    backgroundColor: cardAmount > 0 ? '#FFF7F0' : '#F9FAFB',
                    borderRight: '1px solid ' + (cardAmount > 0 ? '#F58220' : '#E5E7EB'),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px 0',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}>
                    <CreditCardOutlined style={{ fontSize: '28px', color: cardAmount > 0 ? '#F58220' : '#9CA3AF', transition: 'all 0.2s' }} />
                    <span style={{ fontSize: '15px', fontWeight: 700, color: cardAmount > 0 ? '#F58220' : '#6B7280', transition: 'all 0.2s' }}>CARD</span>
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', fontSize: '24px', color: cardAmount > 0 ? '#111827' : '#9CA3AF', fontWeight: 600, transition: 'all 0.2s' }}>£</span>
                    <input
                      type="number"
                      min={0}
                      value={cardAmount || ''}
                      onChange={(e) => {
                        const val = to2(e.target.value);
                        const max = to2(Math.max(0, to2(grandTotal) - to2(cashAmount) - to2(onlineAmount) - to2(onAccountAmount)));
                        setCardAmount(to2(Math.min(val, max)));
                      }}
                      style={{
                        width: '100%',
                        border: 'none',
                        outline: 'none',
                        margin: 0,
                        padding: '0 20px 0 48px',
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#111827',
                        backgroundColor: 'transparent',
                        fontFamily: 'inherit'
                      }}
                      placeholder="0.00"
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', paddingRight: '16px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        if (grandTotal > 0 && to2(cardAmount) === to2(grandTotal)) {
                          setCardAmount(0);
                        } else {
                          setCardAmount(to2(grandTotal));
                          setCashAmount(0);
                          setOnlineAmount(0);
                          setOnAccountAmount(0);
                        }
                      }}
                      style={{
                        background: grandTotal > 0 && to2(cardAmount) === to2(grandTotal) ? '#F58220' : '#F3F4F6',
                        border: 'none',
                        color: grandTotal > 0 && to2(cardAmount) === to2(grandTotal) ? '#FFF' : '#6B7280',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontSize: '15px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      FULL
                    </button>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'stretch',
                  border: '2px solid ' + (onlineAmount > 0 ? '#F58220' : '#E5E7EB'),
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                  transition: 'all 0.2s',
                  boxShadow: onlineAmount > 0 ? '0 4px 12px rgba(245, 130, 32, 0.15)' : 'none'
                }}>
                  <div style={{
                    width: '110px',
                    backgroundColor: onlineAmount > 0 ? '#FFF7F0' : '#F9FAFB',
                    borderRight: '1px solid ' + (onlineAmount > 0 ? '#F58220' : '#E5E7EB'),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px 0',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}>
                    <GlobalOutlined style={{ fontSize: '28px', color: onlineAmount > 0 ? '#F58220' : '#9CA3AF', transition: 'all 0.2s' }} />
                    <span style={{ fontSize: '15px', fontWeight: 700, color: onlineAmount > 0 ? '#F58220' : '#6B7280', transition: 'all 0.2s' }}>ONLINE</span>
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', fontSize: '24px', color: onlineAmount > 0 ? '#111827' : '#9CA3AF', fontWeight: 600, transition: 'all 0.2s' }}>£</span>
                    <input
                      type="number"
                      min={0}
                      value={onlineAmount || ''}
                      onChange={(e) => {
                        const val = to2(e.target.value);
                        const max = to2(Math.max(0, to2(grandTotal) - to2(cashAmount) - to2(cardAmount) - to2(onAccountAmount)));
                        setOnlineAmount(to2(Math.min(val, max)));
                      }}
                      style={{
                        width: '100%',
                        border: 'none',
                        outline: 'none',
                        margin: 0,
                        padding: '0 20px 0 48px',
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#111827',
                        backgroundColor: 'transparent',
                        fontFamily: 'inherit'
                      }}
                      placeholder="0.00"
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', paddingRight: '16px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        if (grandTotal > 0 && to2(onlineAmount) === to2(grandTotal)) {
                          setOnlineAmount(0);
                        } else {
                          setOnlineAmount(to2(grandTotal));
                          setCardAmount(0);
                          setCashAmount(0);
                          setOnAccountAmount(0);
                        }
                      }}
                      style={{
                        background: grandTotal > 0 && to2(onlineAmount) === to2(grandTotal) ? '#F58220' : '#F3F4F6',
                        border: 'none',
                        color: grandTotal > 0 && to2(onlineAmount) === to2(grandTotal) ? '#FFF' : '#6B7280',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontSize: '15px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      FULL
                    </button>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'stretch',
                  border: '2px solid ' + (cashAmount > 0 ? '#F58220' : '#E5E7EB'),
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                  transition: 'all 0.2s',
                  boxShadow: cashAmount > 0 ? '0 4px 12px rgba(245, 130, 32, 0.15)' : 'none'
                }}>
                  <div style={{
                    width: '110px',
                    backgroundColor: cashAmount > 0 ? '#FFF7F0' : '#F9FAFB',
                    borderRight: '1px solid ' + (cashAmount > 0 ? '#F58220' : '#E5E7EB'),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px 0',
                    gap: '4px',
                    transition: 'all 0.2s'
                  }}>
                    <WalletOutlined style={{ fontSize: '28px', color: cashAmount > 0 ? '#F58220' : '#9CA3AF', transition: 'all 0.2s' }} />
                    <span style={{ fontSize: '15px', fontWeight: 700, color: cashAmount > 0 ? '#F58220' : '#6B7280', transition: 'all 0.2s' }}>CASH</span>
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', fontSize: '24px', color: cashAmount > 0 ? '#111827' : '#9CA3AF', fontWeight: 600, transition: 'all 0.2s' }}>£</span>
                    <input
                      type="number"
                      min={0}
                      value={cashAmount || ''}
                      onChange={(e) => {
                        const val = to2(e.target.value);
                        const max = to2(Math.max(0, to2(grandTotal) - to2(cardAmount) - to2(onlineAmount) - to2(onAccountAmount)));
                        setCashAmount(to2(Math.min(val, max)));
                      }}
                      style={{
                        width: '100%',
                        border: 'none',
                        outline: 'none',
                        margin: 0,
                        padding: '0 20px 0 48px',
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#111827',
                        backgroundColor: 'transparent',
                        fontFamily: 'inherit'
                      }}
                      placeholder="0.00"
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', paddingRight: '16px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        if (grandTotal > 0 && to2(cashAmount) === to2(grandTotal)) {
                          setCashAmount(0);
                        } else {
                          setCashAmount(to2(grandTotal));
                          setCardAmount(0);
                          setOnlineAmount(0);
                          setOnAccountAmount(0);
                        }
                      }}
                      style={{
                        background: grandTotal > 0 && to2(cashAmount) === to2(grandTotal) ? '#F58220' : '#F3F4F6',
                        border: 'none',
                        color: grandTotal > 0 && to2(cashAmount) === to2(grandTotal) ? '#FFF' : '#6B7280',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontSize: '15px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      FULL
                    </button>
                  </div>
                </div>

                {isAccountCustomer && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'stretch',
                    border: '2px solid ' + (onAccountAmount > 0 ? '#F58220' : '#E5E7EB'),
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                    transition: 'all 0.2s',
                    boxShadow: onAccountAmount > 0 ? '0 4px 12px rgba(245, 130, 32, 0.15)' : 'none'
                  }}>
                    <div style={{
                      width: '110px',
                      backgroundColor: onAccountAmount > 0 ? '#FFF7F0' : '#F9FAFB',
                      borderRight: '1px solid ' + (onAccountAmount > 0 ? '#F58220' : '#E5E7EB'),
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '16px 0',
                      gap: '4px',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}>
                      <Popover content={balancePopoverContent} trigger="click" placement="top">
                        <div
                          style={{ position: 'absolute', top: '4px', right: '4px', cursor: 'pointer', zIndex: 10, padding: '2px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <InfoCircleOutlined style={{ fontSize: '16px', color: '#9CA3AF', transition: 'color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.color = '#F58220'} onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'} />
                        </div>
                      </Popover>
                      <FileTextOutlined style={{ fontSize: '28px', color: onAccountAmount > 0 ? '#F58220' : '#9CA3AF', transition: 'all 0.2s', marginTop: '4px' }} />
                      <span style={{ fontSize: '13px', fontWeight: 700, color: onAccountAmount > 0 ? '#F58220' : '#6B7280', transition: 'all 0.2s', textAlign: 'center' }}>
                        ACCOUNT
                      </span>
                    </div>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', fontSize: '24px', color: onAccountAmount > 0 ? '#111827' : '#9CA3AF', fontWeight: 600, transition: 'all 0.2s' }}>£</span>
                      <input
                        type="number"
                        min={0}
                        value={onAccountAmount || ''}
                        onChange={(e) => {
                          const val = to2(e.target.value);
                          const orderRemaining = to2(Math.max(0, to2(grandTotal) - to2(cardAmount) - to2(onlineAmount) - to2(cashAmount)));
                          const max = to2(Math.min(orderRemaining, availableCredit));
                          setOnAccountAmount(to2(Math.min(val, max)));
                        }}
                        style={{
                          width: '100%',
                          border: 'none',
                          outline: 'none',
                          margin: 0,
                          padding: '0 20px 0 48px',
                          fontSize: '32px',
                          fontWeight: 700,
                          color: '#111827',
                          backgroundColor: 'transparent',
                          fontFamily: 'inherit'
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', paddingRight: '16px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          const maxAllowed = to2(Math.min(to2(grandTotal), availableCredit));
                          if (maxAllowed > 0 && to2(onAccountAmount) === maxAllowed) {
                            setOnAccountAmount(0);
                          } else {
                            setOnAccountAmount(maxAllowed);
                            setCardAmount(0);
                            setOnlineAmount(0);
                            setCashAmount(0);
                          }
                        }}
                        style={{
                          background: (availableCredit > 0 && to2(onAccountAmount) > 0 && to2(onAccountAmount) === to2(Math.min(to2(grandTotal), availableCredit))) ? '#F58220' : '#F3F4F6',
                          border: 'none',
                          color: (availableCredit > 0 && to2(onAccountAmount) > 0 && to2(onAccountAmount) === to2(Math.min(to2(grandTotal), availableCredit))) ? '#FFF' : '#6B7280',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          fontSize: '15px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        FULL
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="kiosk-modal-footer" style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                className="kiosk-modal-btn cancel"
                onClick={() => setCheckoutStep('payment_type')}
                disabled={isSubmitting}
              >
                Back to Order
              </button>
              <button
                type="button"
                className="kiosk-modal-btn confirm"
                disabled={isSubmitting || to2(to2(cashAmount) + to2(cardAmount) + to2(onlineAmount) + to2(onAccountAmount)) !== to2(grandTotal)}
                onClick={() => {
                  if (cardAmount > 0) {
                    if (terminalAvailable) {
                      handleTerminalPay(cardAmount);
                    } else {
                      handleOnlinePay(cardAmount, 'card');
                    }
                  } else if (onlineAmount > 0) {
                    handleOnlinePay(onlineAmount, 'online');
                  } else if (cashAmount > 0) {
                    setCheckoutStep('cash_auth');
                  } else if (onAccountAmount > 0) {
                    finalizeOrder(order?.id, order?.code || String(order?.id), { method: 'on_account' });
                  }
                }}
              >
                {isSubmitting ? (
                  <>
                    <LoadingOutlined spin style={{ marginRight: '8px' }} />
                    <span>Processing...</span>
                  </>
                ) : (
                  'Process Payment'
                )}
              </button>
            </div>
          </>
        )}

        {checkoutStep === 'cash_auth' && (
          <>
            <div className="kiosk-modal-header">
              <IdcardOutlined style={{ fontSize: '48px', color: '#F58220' }} />
              <h2>Attendant Authorization</h2>
            </div>

            <div className="kiosk-modal-body" style={{ textAlign: 'center', padding: '24px 20px' }}>
              <div
                style={{
                  background: 'radial-gradient(circle at 50% 0%, #fff7ed 0%, #ffffff 100%)',
                  border: '1px solid #fed7aa',
                  borderRadius: '16px',
                  padding: '20px 16px',
                  marginBottom: '20px',
                  boxShadow: '0 4px 12px rgba(245, 130, 32, 0.05)',
                }}
              >
                <span style={{ fontSize: '13px', color: '#9a3412', fontWeight: 600, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Amount to pay
                </span>
                <div style={{ fontSize: '36px', fontWeight: 900, color: '#ea580c', marginTop: '4px' }}>
                  {formatCurrency(cashAmount)}
                </div>
              </div>

              <p style={{ color: '#4B5563', fontSize: '15px', lineHeight: '1.6', margin: '0 auto 20px', maxWidth: '360px' }}>
                Cash payments must be authorized by a staff member. Please ask an attendant to scan their badge to proceed.
              </p>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  margin: '0 auto 28px',
                  maxWidth: '320px',
                  backgroundColor: '#FFF7F0',
                  padding: '16px 24px',
                  borderRadius: '12px',
                  border: '1px solid #FED7AA',
                }}
              >
                {isSubmitting ? (
                  <LoadingOutlined spin style={{ fontSize: '28px', color: '#F58220' }} />
                ) : (
                  <IdcardOutlined style={{ fontSize: '28px', color: '#F58220' }} />
                )}
                <span style={{ fontSize: '17px', fontWeight: 700, color: '#9A3412', textAlign: 'left' }}>
                  {isSubmitting ? 'Authorizing...' : 'Waiting for badge scan...'}
                </span>
              </div>

              <div style={{ position: 'relative', margin: '28px 0 24px' }}>
                <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px solid #E5E7EB' }}></div>
                <div style={{ position: 'relative', display: 'inline-block', padding: '0 16px', backgroundColor: '#fff', color: '#9CA3AF', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Or enter manually
                </div>
              </div>

              <ConfigProvider theme={{ token: { colorPrimary: '#F58220', borderRadius: 8, controlHeightLG: 48 } }}>
                <div style={{ display: 'flex', gap: '12px', maxWidth: '320px', margin: '0 auto' }}>
                  <Input
                    size="large"
                    placeholder="Attendant code"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    onPressEnter={() => manualCode.trim() && handleCashScan(manualCode.trim())}
                    disabled={isSubmitting}
                    style={{ flex: 1, border: '2px solid #E5E7EB', boxShadow: 'none', fontSize: '16px', fontWeight: 500 }}
                  />
                  <Button
                    type="primary"
                    size="large"
                    style={{ padding: '0 24px', fontWeight: 700, fontSize: '16px', boxShadow: 'none' }}
                    disabled={isSubmitting || !manualCode.trim()}
                    onClick={() => handleCashScan(manualCode.trim())}
                  >
                    Verify
                  </Button>
                </div>
              </ConfigProvider>

              {cashAuthError && (
                <p style={{ color: '#EF4444', fontSize: '14px', marginTop: '16px', fontWeight: 600 }}>
                  {cashAuthError}
                </p>
              )}
            </div>

            <div className="kiosk-modal-footer">
              <button
                type="button"
                className="kiosk-modal-btn cancel"
                onClick={() => {
                  setCashAuthError('');
                  setManualCode('');
                  setCheckoutStep('payment_type');
                }}
                disabled={isSubmitting}
                style={{ width: '100%', fontSize: '16px', padding: '14px' }}
              >
                Back
              </button>
            </div>
          </>
        )}

        {checkoutStep === 'payment' && (
          <>
            <div className="kiosk-modal-header">
              {currentProcessingType === 'online' ? (
                <GlobalOutlined style={{ fontSize: '48px', color: '#F58220' }} />
              ) : (
                <CreditCardOutlined style={{ fontSize: '48px', color: '#F58220' }} />
              )}
              <h2>{currentProcessingType === 'online' ? 'Pay by Link' : 'Card Payment'}</h2>
            </div>

            <div className="kiosk-modal-body" style={{ textAlign: 'center', padding: '24px 20px' }}>
              <div
                style={{
                  background: 'radial-gradient(circle at 50% 0%, #fff7ed 0%, #ffffff 100%)',
                  border: '1px solid #fed7aa',
                  borderRadius: '16px',
                  padding: '20px 16px',
                  marginBottom: '20px',
                  boxShadow: '0 4px 12px rgba(245, 130, 32, 0.05)',
                }}
              >
                <span style={{ fontSize: '13px', color: '#9a3412', fontWeight: 600, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Amount to pay
                </span>
                <div style={{ fontSize: '36px', fontWeight: 900, color: '#ea580c', marginTop: '4px' }}>
                  {formatCurrency(processingAmount)}
                </div>
              </div>

              <p style={{ color: '#4B5563', fontSize: '15px', lineHeight: '1.6', margin: '0 auto 24px', maxWidth: '360px' }}>
                {cardMethod === 'terminal'
                  ? 'Please follow the instructions on the card reader. Tap, insert, or swipe your card to complete payment.'
                  : 'Complete your payment in the window that just opened.'}
              </p>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  margin: '0 auto',
                  maxWidth: '320px',
                  backgroundColor: '#FFF7F0',
                  padding: '16px 24px',
                  borderRadius: '12px',
                  border: '1px solid #FED7AA',
                }}
              >
                <SyncOutlined spin style={{ fontSize: '28px', color: '#F58220' }} />
                <span style={{ fontSize: '17px', fontWeight: 700, color: '#9A3412', textAlign: 'left' }}>
                  {cardMethod === 'terminal' ? 'Waiting for Card Terminal...' : 'Waiting for Payment...'}
                </span>
              </div>
            </div>

            <div className="kiosk-modal-footer">
              <button
                type="button"
                className="kiosk-modal-btn cancel"
                onClick={cancelPayment}
                style={{ width: '100%', fontSize: '16px', padding: '14px' }}
              >
                Cancel Payment
              </button>
            </div>
          </>
        )}

        {checkoutStep === 'payment_failed' && (
          <>
            <div className="kiosk-modal-header" style={{ backgroundColor: '#FEF2F2' }}>
              <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#EF4444' }} />
              <h2 style={{ color: '#EF4444' }}>Payment Unsuccessful</h2>
            </div>

            <div className="kiosk-modal-body" style={{ textAlign: 'center', padding: '32px 20px' }}>
              <p style={{ color: '#4B5563', fontSize: '16px', marginBottom: '20px' }}>
                {paymentErrorMessage || 'The payment was declined or cancelled. Please try again.'}
              </p>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#111827', marginBottom: '8px' }}>
                {formatCurrency(processingAmount)}
              </div>
            </div>

            <div className="kiosk-modal-footer" style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                className="kiosk-modal-btn cancel"
                onClick={() => setCheckoutStep('review')}
                disabled={isSubmitting}
                style={{ fontSize: '16px' }}
              >
                Back to Order
              </button>
              <button
                type="button"
                className="kiosk-modal-btn confirm"
                onClick={handleCardRetry}
                disabled={isSubmitting}
                style={{ fontSize: '16px' }}
              >
                {isSubmitting ? (
                  <>
                    <LoadingOutlined spin />
                    <span>Connecting...</span>
                  </>
                ) : (
                  'Try Again'
                )}
              </button>
            </div>
          </>
        )}

        {checkoutStep === 'processing' && (
          <div
            className="kiosk-modal-body"
            style={{ textAlign: 'center', padding: '80px 20px 100px' }}
          >
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              backgroundColor: '#FFF7F0',
              marginBottom: '32px',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '-16px', left: '-16px', right: '-16px', bottom: '-16px',
                borderRadius: '50%',
                border: '2px solid #FFEDD5',
                opacity: 0.6
              }}></div>
              <LoadingOutlined
                style={{
                  fontSize: '56px',
                  color: '#F58220',
                }}
              />
            </div>
            <h2 style={{ fontSize: '32px', margin: '0 0 16px', fontWeight: 800, color: '#111827', letterSpacing: '-0.02em' }}>
              Processing Payment...
            </h2>
            <p
              style={{
                color: '#6B7280',
                fontSize: '18px',
                margin: '0 auto',
                lineHeight: '1.6',
                fontWeight: 500,
                maxWidth: '360px'
              }}
            >
              Please wait while we securely process your transaction and prepare your order.
            </p>
          </div>
        )}

        {checkoutStep === 'success' && (
          <div
            className="kiosk-modal-body"
            style={{ textAlign: 'center', padding: '80px 20px 100px' }}
          >
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              backgroundColor: '#ECFDF5',
              marginBottom: '32px',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '-16px', left: '-16px', right: '-16px', bottom: '-16px',
                borderRadius: '50%',
                border: '2px solid #D1FAE5',
                opacity: 0.6
              }}></div>
              <CheckCircleFilled
                style={{
                  fontSize: '64px',
                  color: '#10B981',
                }}
              />
            </div>
            <h2 style={{ fontSize: '36px', margin: '0 0 16px', fontWeight: 900, color: '#111827', letterSpacing: '-0.02em' }}>
              Order Successful!
            </h2>
            <p
              style={{
                color: '#6B7280',
                fontSize: '18px',
                margin: '0 auto',
                lineHeight: '1.6',
                fontWeight: 500,
                maxWidth: '360px'
              }}
            >
              Thank you for shopping with us. Please collect your receipt below.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default KioskCheckoutModal;
